import { useCallback, useEffect, useRef, useState } from "react";

// The session lifecycle of a V2 onboarding wizard (athlete or coach). It
// keeps the wizard to the account this tab opened with: another tab that
// logs out, or logs in as someone else, stops it (sessionLost), and nothing
// it still shows is written to the other account. `alive()` is false once
// the wizard is done for (unmounted, logging out or stopped), so work still
// in flight moves no step and saves nothing; `end()` marks a wizard that is
// logging out, `stop()` one whose session changed.
//
// `binding` is { bindSession, sessionChanged } from the wizard's own
// services module (each re-exports ./sessionBinding), so a wizard reaches
// its session through one module. Call the hook before the wizard's first
// read: its effect binds ahead of the effects declared after it.
export const useWizardSession = ({ bindSession, sessionChanged }) => {
  const aliveRef = useRef(true);
  const [sessionLost, setSessionLost] = useState(false);
  const stop = useCallback(() => {
    aliveRef.current = false;
    setSessionLost(true);
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    const release = bindSession(stop);
    const onStorage = () => {
      if (sessionChanged()) stop();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      aliveRef.current = false;
      if (typeof release === "function") release();
      window.removeEventListener("storage", onStorage);
    };
  }, [bindSession, sessionChanged, stop]);

  const alive = useCallback(() => aliveRef.current, []);
  const end = useCallback(() => {
    aliveRef.current = false;
  }, []);
  return { alive, end, stop, sessionLost };
};
